import React, { createContext, useCallback, useContext, useRef, useMemo } from 'react';

import { getLogger } from '@confluence/logger';

type DraftSyncSuccessListener = (contentId: string) => void;

const logger = getLogger('native-collab');

type DraftSyncAndPublishContextType = {
	getIsPublishing: () => boolean;
	setIsPublishing: (value: boolean) => void;
	isPublishing: boolean;
	subscribeDraftSyncSuccess: (listener: DraftSyncSuccessListener) => () => void;
	notifyDraftSyncSuccess: (contentId: string) => void;
};
const DraftSyncAndPublishContext = createContext<DraftSyncAndPublishContextType>({
	getIsPublishing: () => false,
	setIsPublishing: (_: boolean) => {
		// Intentional no-op
	},
	isPublishing: false,
	subscribeDraftSyncSuccess: () => () => {},
	notifyDraftSyncSuccess: () => {},
});

export const DraftSyncAndPublishContextProvider = ({
	children,
}: {
	children: any;
}): React.JSX.Element => {
	// These should remain in sync. The ref is used while a draft sync occurs, while
	// the state variable is used for re-rendering.
	const isPublishingRef = useRef<boolean>(false);
	const [isPublishing, setIsPublishing] = React.useState(false);

	const draftSyncSuccessListeners = useRef(new Set<DraftSyncSuccessListener>());

	const subscribeDraftSyncSuccess = useCallback((listener: DraftSyncSuccessListener) => {
		draftSyncSuccessListeners.current.add(listener);
		return () => {
			draftSyncSuccessListeners.current.delete(listener);
		};
	}, []);

	const notifyDraftSyncSuccess = useCallback((contentId: string) => {
		draftSyncSuccessListeners.current.forEach((listener) => {
			try {
				listener(contentId);
			} catch (error) {
				// An observer failure must not turn a successful draft save into a sync error.
				logger.error`Draft sync success listener failed: ${error}`;
			}
		});
	}, []);

	const getIsPublishing = useCallback(() => isPublishingRef.current, []);

	const setPublishing = useCallback((value: boolean) => {
		isPublishingRef.current = value;
		setIsPublishing(value);
	}, []);

	const contextValue = useMemo(
		() => ({
			getIsPublishing,
			setIsPublishing: setPublishing,
			isPublishing,
			subscribeDraftSyncSuccess,
			notifyDraftSyncSuccess,
		}),
		[
			getIsPublishing,
			setPublishing,
			isPublishing,
			subscribeDraftSyncSuccess,
			notifyDraftSyncSuccess,
		],
	);

	return (
		<DraftSyncAndPublishContext.Provider value={contextValue}>
			{children}
		</DraftSyncAndPublishContext.Provider>
	);
};

export const DraftSyncAndPublishContextConsumer = ({ children }: { children: any }): any => {
	const context = useDraftSyncAndPublishContext();
	return children(context);
};

export const useDraftSyncAndPublishContext = (): DraftSyncAndPublishContextType => {
	return useContext(DraftSyncAndPublishContext);
};
